import {
  BlockType,
  ColorSchemeSchema,
  FieldGroupSchema,
  FieldRole,
  ImageFieldSchema,
  ResourceListFieldSchema,
  ResourceSchema,
  RichTextFieldSchema,
  UrlFieldSchema,
} from './Types';
import { z } from 'zod';

export const MagicResourceLibraryStyleOptionsSchema = z.object({
  backgroundColor: z.string().optional(),
});

export type MagicResourceLibraryStyleOptions = z.infer<typeof MagicResourceLibraryStyleOptionsSchema>;

export const MagicResourceLibraryOptionsSchema = z.object({
  colorScheme: ColorSchemeSchema.optional(),
  preheaderEnabled: z.boolean().optional(),
  subheaderEnabled: z.boolean().optional(),
  style: MagicResourceLibraryStyleOptionsSchema.optional(),
  layout: z.string().optional(),
});

export type MagicResourceLibraryOptions = z.infer<typeof MagicResourceLibraryOptionsSchema>;

export const MagicResourceLibraryTopLevelPreheaderFieldSchema = RichTextFieldSchema.extend({
  name: z.literal('preheader'),
  role: z.literal(FieldRole.Preheader),
});

export const MagicResourceLibraryTopLevelHeaderFieldSchema = RichTextFieldSchema.extend({
  name: z.literal('header'),
  role: z.literal(FieldRole.Header),
});

export const MagicResourceLibraryTopLevelSubheaderFieldSchema = RichTextFieldSchema.extend({
  name: z.literal('subheader'),
  role: z.literal(FieldRole.Subheader),
});

export const MagicResourceLibraryHeaderFieldSchema = RichTextFieldSchema.extend({
  name: z.literal('header'),
  role: z.literal(FieldRole.Header),
});

export const MagicResourceLibraryDescriptionFieldSchema = RichTextFieldSchema.extend({
  name: z.literal('description'),
  role: z.literal(FieldRole.Description),
});

export const MagicResourceLibraryImageFieldSchema = ImageFieldSchema.extend({
  name: z.literal('image'),
  role: z.literal(FieldRole.Image),
});

export const MagicResourceLibraryLinkFieldSchema = UrlFieldSchema.extend({
  name: z.literal('link'),
  role: z.literal(FieldRole.Url),
});

export const MagicResourceLibraryTopLevelFieldSchema = z.discriminatedUnion('name', [
  MagicResourceLibraryTopLevelPreheaderFieldSchema,
  MagicResourceLibraryTopLevelHeaderFieldSchema,
  MagicResourceLibraryTopLevelSubheaderFieldSchema,
]);

export type MagicResourceLibraryTopLevelField = z.infer<typeof MagicResourceLibraryTopLevelFieldSchema>;

export const MagicResourceLibraryTopLevelFieldGroupSchema = FieldGroupSchema.extend({
  fields: z.array(MagicResourceLibraryTopLevelFieldSchema),
});
export type MagicResourceLibraryTopLevelFieldGroup = z.infer<typeof MagicResourceLibraryTopLevelFieldGroupSchema>;

export const MagicResourceLibraryResourceFieldsGroupSchema = FieldGroupSchema.extend({
  fields: z.array(
    ResourceListFieldSchema.extend({
      name: z.literal('resourceList'),
      role: z.literal(FieldRole.ResourceList),
    }),
  ),
});
export type MagicResourceLibraryResourceFieldsGroup = z.infer<typeof MagicResourceLibraryResourceFieldsGroupSchema>;

export const MagicResourceBlockConfigSchema = z.object({
  options: MagicResourceLibraryOptionsSchema,
  type: z.literal(BlockType.MagicResourceLibrary),
  fieldGroups: z
    .tuple([MagicResourceLibraryTopLevelFieldGroupSchema])
    .rest(MagicResourceLibraryResourceFieldsGroupSchema),
  version: z.literal(2),
});

export type Resource = z.infer<typeof ResourceSchema>;
